import { msg } from "@lingui/core/macro";
import {
  batch,
  type Component,
  type ComponentProps,
  createComputed,
  createEffect,
  createMemo,
  createSignal,
  For,
  onCleanup,
  Show,
  untrack,
} from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { SortableProvider } from "@thisbeyond/solid-dnd";
import { keepPreviousData } from "@tanstack/solid-query";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import type { PowerSyncDrizzleDB, Todo } from "@listto/powersync";
import { createPowerSyncQuery, type PowerSyncQueryFn } from "@listto/shared/solid";
import {
  cn,
  type createTodoMutation,
  getListsWithMetaQuery,
  type getMyDayTodosCountQuery,
  type getTodosCountQuery,
  type ListWithMeta,
  queryKeys,
} from "@listto/shared";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useLocation, useNavigate } from "@tanstack/solid-router";
import { EmptyState } from "@/components/ui/empty-state";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";
import { useTodoFocus } from "../hooks/use-todo-focus";
import { useDrawerSwipeNav } from "../hooks/use-drawer-swipe-nav";
import { useAppLayoutContext } from "@/routes/_app/route";
import { useListShortcuts } from "../hooks/use-list-shortcuts";
import { useTodoListEvents } from "../hooks/use-todo-list-events";
import { useTodoSelection } from "../hooks/use-todo-selection";
import { useElementHeight } from "../hooks/use-element-height";
import { animatedLists, useInitialAnimation } from "../hooks/use-initial-animation";
import { useInfiniteScroll } from "../hooks/use-infinite-scroll";
import { useScrollObserver } from "../hooks/use-scroll-observer";
import { usePathnameScrollReset } from "../hooks/use-pathname-scroll-reset";
import { useListTitleEdit } from "../hooks/use-list-title-edit";
import { EXIT_DURATION_MS, useTodoMutations } from "../hooks/use-todo-mutations";
import type { TodoVirtualItem, VirtualRow } from "../types";
import { TodoListSkeleton } from "./todo-list-skeleton";
import { CompletedHeaderRowItem, CompletedRowItem, PendingRowItem } from "./todo-list-row-items";
import { TodoListStickyHeader } from "./todo-list-sticky-header";
import { TodoListMainHeader } from "./todo-list-main-header";
import { TodoListAddBar } from "./todo-list-add-bar";
import { useScope, useWhenContext } from "@/lib/shortcuts/solid";
import { Deferred } from "@/components/ui/deferred";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import {
  EST_ROW_HEADER,
  EST_ROW_TODO,
  EST_ROW_TODO_WITH_BADGES,
  findFirstVisibleRow,
  getTodoNavigationTarget,
  INITIAL_CONTAINER_HEIGHT,
  INNER_DIV_PT_PX,
  MIN_STICKY_THRESHOLD_PX,
  type MUTATION_OPTIONS_INDEX,
  OVERSCAN_PX,
  ROW_GAP_PX,
  rowKey,
  STICKY_HEADER_OFFSET_PX,
  TODO_PAGE_SIZE,
  todosSignature,
} from "./todo-list-view.helpers";

// ── Props ─────────────────────────────────────────────────────────────────────

interface TodoListViewProps {
  title: string;
  icon: Component<ComponentProps<"svg">>;
  userId: string | undefined;
  queryFn: (
    db: PowerSyncDrizzleDB,
    userId: string,
    options?: { limit?: number },
  ) => ReturnType<PowerSyncQueryFn<Todo>>;
  countQueryFn: (
    db: PowerSyncDrizzleDB,
    userId: string,
  ) => ReturnType<typeof getTodosCountQuery> | ReturnType<typeof getMyDayTodosCountQuery>;
  emptyMessage?: string;
  showAddTodo?: boolean;
  defaultAddTodoProps?: Parameters<typeof createTodoMutation>[typeof MUTATION_OPTIONS_INDEX];
  listId: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TodoListView(props: TodoListViewProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const navigate = useNavigate();
  const location = useLocation();
  const searchPalette = useSearchPalette();
  const { lastFocusedTodoInput, setLastFocusedTodoInput } = useTodoFocus();
  const { setEntranceDirection, focusSlide } = useDrawerSwipeNav();
  const { activeDragId, openEditListDialog } = useAppLayoutContext();

  // ── Local signals ─────────────────────────────────────────────────────────

  const [todoInput, setTodoInput] = createSignal<HTMLInputElement | null>(null);
  const [completedCollapsed, setCompletedCollapsed] = createSignal(true);
  const [hasAnimated, setHasAnimated] = createSignal(
    untrack(() => animatedLists.has(props.listId)),
  );
  const [todosLimit, setTodosLimit] = createSignal(TODO_PAGE_SIZE);
  const [todoSentinelRef, setTodoSentinelRef] = createSignal<HTMLDivElement | null>(null);
  const [scrollContainerRef, setScrollContainerRef] = createSignal<HTMLDivElement | null>(null);
  const [focusedIndex, setFocusedIndex] = createSignal<number | null>(null);
  const [isKeyboardFocus, setIsKeyboardFocus] = createSignal(false);
  const [scrollTop, setScrollTop] = createSignal(0);
  const [containerHeight, setContainerHeight] = createSignal(INITIAL_CONTAINER_HEIGHT);
  let headerRef: HTMLDivElement | undefined = undefined;

  // ── Dynamic row measurement ─────────────────────────────────────────────
  // Real, content-driven heights keyed by a stable id (todo id, or a constant
  // for the single completed-header). Persisting them across viewport exits
  // means re-entering rows reposition without a flash.
  const [measuredHeights, setMeasuredHeights] = createStore<Record<string, number>>({});

  // Measurement writes are coalesced into a single batched store update per
  // animation frame. While scrolling into new rows many measurements land in
  // the same frame; flushing them together means `rowPositions` recomputes at
  // most once per frame instead of once per row.
  let pendingHeights: Record<string, number> | null = null;
  let flushHandle = 0;

  const flushMeasurements = () => {
    flushHandle = 0;
    const pending = pendingHeights;
    pendingHeights = null;
    if (!pending) {
      return;
    }
    batch(() => {
      for (const [key, height] of Object.entries(pending)) {
        if (measuredHeights[key] !== height) {
          setMeasuredHeights(key, height);
        }
      }
    });
  };

  const measureRow = (key: string, el: HTMLDivElement) => {
    // The ResizeObserver stays attached while the row is mounted so a real
    // height change (e.g. badges added/removed after a sync from another
    // device) is picked up. Plain scrolling never changes a row's box size,
    // so already-measured rows stay quiet — no churn.
    const update = () => {
      const h = el.offsetHeight;
      if (h <= 0 || measuredHeights[key] === h) {
        return;
      }
      (pendingHeights ??= {})[key] = h;
      if (!flushHandle) {
        flushHandle = requestAnimationFrame(flushMeasurements);
      }
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    onCleanup(() => {
      ro.disconnect();
    });
  };

  onCleanup(() => {
    if (flushHandle) {
      cancelAnimationFrame(flushHandle);
    }
  });

  // ── Queries ───────────────────────────────────────────────────────────────

  const todos = createPowerSyncQuery<Todo>({
    queryKey: () =>
      queryKeys.todos.listPaged({ listId: props.listId, userId: props.userId }, todosLimit()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = props.userId;
      const limitVal = todosLimit();
      return dbInstance && uid ? props.queryFn(dbInstance, uid, { limit: limitVal }) : null;
    },
    enabled: () => Boolean(props.userId) && Boolean(drizzle()),
    placeholderData: (prev) => prev,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const [stableTodos, setStableTodos] = createSignal<Todo[] | undefined>(undefined);
  // Signature of what `stableTodos` currently holds, so we can ignore refetches
  // that carry identical data (see `todosSignature`).
  let stableSignature: string | undefined = undefined;
  let loadedListId: string | undefined = undefined;

  // IDs present at the list's first paint. Only these rows are eligible for the
  // staggered entrance animation — a row that mounts later (a brand-new todo, or
  // a virtualized row scrolled back into view) must never replay the entrance.
  const [initialAnimIds, setInitialAnimIds] = createSignal<Set<string>>(new Set());

  // Clear stableTodos and show skeleton immediately when switching lists
  createComputed(() => {
    // Track props.listId reactively
    void props.listId;
    stableSignature = undefined;
    setStableTodos(undefined);
    loadedListId = undefined;
    setInitialAnimIds(new Set<string>());
  });

  // Update stableTodos when query data arrives — but only when it differs from
  // what we already show, so a mutation's burst of identical refetches collapses
  // into a single update (no needless rowData/virtualItems recompute or flicker).
  createEffect(() => {
    const { data, isPlaceholderData } = todos;
    if (data === undefined) {
      return;
    }
    // If the data is placeholder data and we have reset the loadedListId (meaning we switched lists),
    // we must ignore this placeholder data because it belongs to the previous list.
    if (isPlaceholderData && loadedListId === undefined) {
      return;
    }
    const signature = todosSignature(data);
    if (signature === stableSignature) {
      return;
    }
    const isFirstLoad = loadedListId === undefined;
    stableSignature = signature;
    loadedListId = props.listId;
    if (isFirstLoad) {
      // Snapshot the first-paint ids so only they can run the entrance.
      setInitialAnimIds(new Set(data.map((todo) => todo.id)));
    } else if (!untrack(hasAnimated)) {
      // A genuine data change after the first paint (the user's first action)
      // ends the entrance window *now*. Otherwise the burst of PowerSync
      // invalidations a mutation triggers remounts virtualized rows while
      // `isInitialLoad` is still true, replaying `animate-todo-entrance` 2–3
      // times — the visible flicker. Closing the window here means the rows the
      // burst remounts are no longer eligible to animate.
      animatedLists.add(props.listId);
      setHasAnimated(true);
    }
    setStableTodos(data);
  });

  const currentListTodos = () => stableTodos();

  const lists = createPowerSyncQuery<ListWithMeta>({
    queryKey: () => queryKeys.lists.customLists(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = props.userId;
      return dbInstance && uid ? getListsWithMetaQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(props.userId) && Boolean(drizzle()),
    placeholderData: keepPreviousData,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // ── Derived data ──────────────────────────────────────────────────────────

  const currentList = () => lists.data?.find((l) => l.id === props.listId);
  const pendingTodos = () => (currentListTodos() ?? []).filter((todo) => todo.completed !== 1);
  const completedTodos = () => (currentListTodos() ?? []).filter((todo) => todo.completed === 1);
  const isInitialLoad = () => !hasAnimated() && (currentListTodos()?.length ?? 0) > 0;
  // A row plays the entrance only while the window is open *and* it was part of
  // the first-paint snapshot — never on later remounts/new items.
  const rowIsInitialLoad = (row: VirtualRow) =>
    isInitialLoad() && row.kind !== "completed-header" && initialAnimIds().has(row.todo.id);

  /** Flat virtual row array: pending items + optional completed section */
  const rowData = createMemo<VirtualRow[]>(() => {
    const rows: VirtualRow[] = pendingTodos().map((todo) => ({ kind: "pending", todo }));
    const completed = completedTodos();
    if (completed.length > 0) {
      rows.push({ kind: "completed-header", count: completed.length });
      if (!completedCollapsed()) {
        for (const todo of completed) {
          rows.push({ kind: "completed", todo });
        }
      }
    }
    return rows;
  });

  // ── Navigation ────────────────────────────────────────────────────────────

  const handleSelectTodo = (id: string, options?: { replace?: boolean }) => {
    const target = getTodoNavigationTarget(props.listId, id);
    void navigate({ ...target, replace: options?.replace ?? false });
  };

  const handleItemFocus = (id: string) => {
    const idx = rowData().findIndex(
      (r) => (r.kind === "pending" || r.kind === "completed") && r.todo.id === id,
    );
    if (idx !== -1) {
      setFocusedIndex(idx);
    }
  };

  const focusedTodoId = () => {
    const idx = focusedIndex();
    if (idx === null) {
      return undefined;
    }
    const row = rowData()[idx];
    return row && (row.kind === "pending" || row.kind === "completed") ? row.todo.id : undefined;
  };

  // ── Mutations & selection ─────────────────────────────────────────────────

  const {
    newTodoText,
    setNewTodoText,
    isSubmitting,
    newlyAddedIds,
    exitingIds,
    setExitingIds,
    todoToDelete,
    setTodoToDelete,
    handleAddTodo,
    handleToggleTodo,
    handleDeleteTodo,
    executeDeleteTodo,
  } = useTodoMutations({
    userId: () => props.userId,
    listId: () => props.listId,
    defaultAddTodoProps: () => props.defaultAddTodoProps,
    drizzle,
    todoInput,
    currentListTodos,
  });

  const {
    isSelectionMode,
    selectedIds,
    setSelectedIds,
    toggleSelectionMode,
    toggleItemSelection,
    handleBulkDelete,
    handleBulkImportant,
    handleBulkMove,
  } = useTodoSelection({ drizzle, setExitingIds });

  const {
    isEditingTitle,
    setIsEditingTitle,
    tempTitle,
    setTempTitle,
    handleStartEditTitle,
    handleSaveTitle,
    optimisticTitle,
  } = useListTitleEdit({ currentList, drizzle });

  const effectiveTitle = () => optimisticTitle() ?? props.title;

  // ── Shortcut engine ───────────────────────────────────────────────────────

  useScope("list");
  useWhenContext({
    "todo.focused": () => focusedIndex() !== null,
    "selection.active": () => isSelectionMode(),
  });

  useListShortcuts({
    rowData,
    focusedIndex,
    setFocusedIndex,
    setIsKeyboardFocus,
    drizzle,
    toggleSelectionMode: () => {
      toggleSelectionMode();
    },
    setSelectedIds,
    currentListTodos,
  });

  useTodoListEvents({
    rowData,
    focusedIndex,
    setFocusedIndex,
    setIsKeyboardFocus,
    todoInput,
    todoToDelete,
    searchPalette,
    setEntranceDirection,
    lastFocusedTodoInput,
    setLastFocusedTodoInput,
    completedCollapsed,
    setCompletedCollapsed,
    scrollContainerRef,
    isSelectionMode,
    toggleItemSelection,
    handleSelectTodo,
    handleToggleTodo,
    handleDeleteTodo,
    drizzle,
    location,
    navigate,
    EXIT_DURATION_MS,
    isCustomList: () => Boolean(currentList()),
  });

  // ── Focus effects ─────────────────────────────────────────────────────────

  // Clamp / reset focusedIndex when list size changes
  createEffect(() => {
    const total = rowData().length;
    const current = focusedIndex();
    if (current !== null) {
      if (total === 0) {
        setFocusedIndex(null);
      } else if (current >= total) {
        setFocusedIndex(total - 1);
      }
    }
  });

  // Sync focusedIndex with the active todo in the URL detail drawer
  createEffect(() => {
    const match = /\/todo\/([^/]+)/.exec(location().pathname);
    if (match) {
      const idx = rowData().findIndex(
        (r) => (r.kind === "pending" || r.kind === "completed") && r.todo.id === match[1],
      );
      if (idx !== -1 && focusedIndex() !== idx) {
        setFocusedIndex(idx);
      }
    }
  });

  // ── Virtualization ────────────────────────────────────────────────────────
  //
  // WHY reconcile + store?
  // @thisbeyond/solid-dnd registers draggable/droppable on mount and removes them
  // on cleanup. solid-js <For> reconciles by object identity (===). If virtualItems()
  // returns new wrapper objects on every recompute, <For> sees all items as new →
  // unmounts old ones → removeDraggable/removeDroppable fires for IDs that never
  // passed through onMount. Using reconcile() keeps object references stable when
  // only position/scroll changes, so only genuinely new/removed items cycle.

  const headerHeight = useElementHeight(() => headerRef);
  const totalHeaderOffset = () => INNER_DIV_PT_PX + headerHeight();
  const isHeaderSticky = () =>
    scrollTop() > Math.max(MIN_STICKY_THRESHOLD_PX, headerHeight() - STICKY_HEADER_OFFSET_PX);

  // Estimate used only before a row has been measured by the ResizeObserver.
  const getEstimatedRowHeight = (row: VirtualRow) => {
    if (row.kind === "completed-header") {
      return EST_ROW_HEADER;
    }
    const { todo } = row;
    const hasListName =
      (props.listId === "all" || props.listId === "planned") &&
      Boolean(lists.data?.find((l) => l.id === todo.list_id)?.name);

    const hasBadges = Boolean(
      todo.due_date ?? todo.my_day_date ?? todo.recurrence_rule ?? hasListName,
    );
    return hasBadges ? EST_ROW_TODO_WITH_BADGES : EST_ROW_TODO;
  };

  // Prefer the measured height; fall back to the estimate while unmeasured.
  const getRowHeight = (row: VirtualRow) =>
    measuredHeights[rowKey(row)] ?? getEstimatedRowHeight(row);

  const rowPositions = createMemo(() => {
    let currentTop = 0;
    return rowData().map((row) => {
      const height = getRowHeight(row);
      const start = currentTop;
      // Add the uniform gap after every row so the separation between any two
      // adjacent todos is always exactly ROW_GAP_PX, regardless of content.
      currentTop += height + ROW_GAP_PX;
      return { start, height };
    });
  });

  const totalHeight = () => {
    const positions = rowPositions();
    const last = positions.at(-1);
    return last ? last.start + last.height : 0;
  };

  const virtualItems = createMemo<TodoVirtualItem[]>(() => {
    const st = scrollTop();
    const ch = containerHeight();
    const h = totalHeaderOffset();
    const adjustedSt = Math.max(0, st - h);
    const positions = rowPositions();
    const rows = rowData();
    const result: TodoVirtualItem[] = [];

    const windowTop = adjustedSt - OVERSCAN_PX;
    const windowBottom = adjustedSt + ch + OVERSCAN_PX;
    const firstVisible = findFirstVisibleRow(positions, windowTop);

    for (let i = firstVisible; i < positions.length; i += 1) {
      const pos = positions[i];
      if (!pos) {
        continue;
      }
      // Positions are sorted by `start`; once we pass the window bottom every
      // subsequent row is below it too, so we can stop scanning.
      if (pos.start >= windowBottom) {
        break;
      }
      {
        const row = rows[i];
        if (!row) {
          continue;
        }
        const id = row.kind === "completed-header" ? `header-${i}` : `todo-${row.todo.id}`;
        result.push({ id, index: i, start: pos.start, size: pos.height, row });
      }
    }
    return result;
  });

  const [virtualItemsStore, setVirtualItemsStore] = createStore<TodoVirtualItem[]>([]);
  createEffect(() => {
    setVirtualItemsStore(reconcile(virtualItems(), { key: "id" }));
  });

  // Accumulative sortable IDs — retains IDs that left the viewport until their
  // onCleanup fires, preventing stale removeDraggable calls.
  const [sortableIds, setSortableIds] = createSignal<string[]>([]);
  const knownIds = new Set<string>();

  createComputed(() => {
    // oxlint-disable-next-line prefer-destructuring
    const listId = props.listId;
    untrack(() => {
      setHasAnimated(animatedLists.has(listId));
      knownIds.clear();
      setSortableIds([]);
      setTodosLimit(TODO_PAGE_SIZE);
    });
  });

  createEffect(() => {
    const data = currentListTodos();
    if (data && !activeDragId()) {
      for (const todo of data) {
        knownIds.add(`todo-${todo.id}`);
      }
      setSortableIds([...knownIds]);
    }
  });

  // Auto-scroll focused item into view and restore DOM focus
  createEffect(() => {
    const idx = focusedIndex();
    if (idx === null) {
      return;
    }

    const container = scrollContainerRef();
    if (container && isKeyboardFocus()) {
      const pos = untrack(() => rowPositions()[idx]);
      if (pos) {
        const containerH = container.clientHeight;
        const targetScroll = pos.start + totalHeaderOffset() + pos.height / 2 - containerH / 2;
        container.scrollTo({ top: targetScroll, behavior: "smooth" });
      }
    }

    requestAnimationFrame(() => {
      const row = untrack(() => rowData()[idx]);
      if (!row) {
        return;
      }

      if (row.kind === "completed-header") {
        const el = document.querySelector(`#completed-header-${idx}`);
        if (el && lastFocusedTodoInput() === "row") {
          if (el instanceof HTMLElement) {
            el.focus();
          }
          setLastFocusedTodoInput(null);
        }
      } else {
        const el = document.querySelector(`#todo-item-${row.todo.id}`);
        if (el && lastFocusedTodoInput() === "row") {
          const focusable = el.querySelector('[tabindex="0"]');
          if (focusable instanceof HTMLElement) {
            focusable.focus();
          }
          setLastFocusedTodoInput(null);
        }
      }
    });
  });

  // ── Side effects ──────────────────────────────────────────────────────────

  useInitialAnimation({
    listId: () => props.listId,
    hasAnimatedAccessor: hasAnimated,
    setHasAnimated,
    currentListTodosAccessor: currentListTodos,
  });

  useInfiniteScroll({
    sentinelAccessor: todoSentinelRef,
    containerAccessor: scrollContainerRef,
    onIntersect: () => {
      const current = currentListTodos();
      if (current && current.length >= todosLimit()) {
        setTodosLimit((l) => l + TODO_PAGE_SIZE);
      }
    },
  });

  usePathnameScrollReset({
    pathnameAccessor: () => location().pathname,
    containerAccessor: scrollContainerRef,
  });

  useScrollObserver({
    containerAccessor: scrollContainerRef,
    setScrollTop,
    setContainerHeight,
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div class="flex flex-1 flex-col min-h-0 overflow-hidden bg-transparent relative">
      {/* Sticky mini-header — slides in when user scrolls past the main header */}
      <TodoListStickyHeader
        isSticky={isHeaderSticky()}
        title={effectiveTitle()}
        icon={props.icon}
        listIcon={currentList()?.icon}
        isSelectionMode={isSelectionMode()}
        selectedCount={selectedIds().size}
        hasItems={(currentListTodos()?.length ?? 0) > 0}
        lists={lists.data}
        listId={props.listId}
        userId={props.userId}
        countQueryFn={props.countQueryFn}
        onToggleSelection={toggleSelectionMode}
        onBulkMove={(targetListId) => void handleBulkMove(targetListId)}
        onBulkImportant={() => void handleBulkImportant(true)}
        onBulkDelete={handleBulkDelete}
      />

      {/* Scroll container */}
      <div
        ref={setScrollContainerRef}
        class={cn(
          "flex flex-1 flex-col min-h-0 [scrollbar-gutter:stable]",
          location().pathname.includes("/todo/") ? "overflow-hidden" : "overflow-y-auto",
        )}
        style={{
          "overflow-anchor": "none",
          "will-change": "scroll-position",
        }}
      >
        <div class="px-6 pt-4 pb-0 min-h-full flex flex-col flex-1 shrink-0">
          <TodoListMainHeader
            ref={(el) => (headerRef = el)}
            currentList={currentList()}
            title={effectiveTitle()}
            icon={props.icon}
            isEditingTitle={isEditingTitle()}
            tempTitle={tempTitle()}
            onTempTitleInput={setTempTitle}
            onStartEditTitle={handleStartEditTitle}
            onSaveTitle={() => void handleSaveTitle()}
            onCancelEditTitle={() => setIsEditingTitle(false)}
            isSelectionMode={isSelectionMode()}
            currentListTodos={currentListTodos()}
            onToggleSelection={toggleSelectionMode}
            onOpenSettings={() => {
              const list = currentList();
              if (list) {
                openEditListDialog(list);
              }
            }}
            listId={props.listId}
            userId={props.userId}
            countQueryFn={props.countQueryFn}
            onEnterList={() => {
              setIsKeyboardFocus(true);
              setLastFocusedTodoInput("row");
              setFocusedIndex(0);
            }}
          />

          {/* Virtualized task list */}
          <SortableProvider ids={sortableIds()}>
            <Show
              when={currentListTodos() !== undefined}
              fallback={
                <Deferred delayMs={800}>
                  <TodoListSkeleton />
                </Deferred>
              }
            >
              <Show
                when={(currentListTodos()?.length ?? 0) > 0}
                fallback={
                  <EmptyState
                    icon={props.icon}
                    title={effectiveTitle()}
                    description={props.emptyMessage ?? t(msg`No pending tasks in this list.`)}
                  />
                }
              >
                <div style={{ height: `${totalHeight()}px`, width: "100%", position: "relative" }}>
                  <For each={virtualItemsStore}>
                    {(virtualItem) => (
                      <div
                        ref={(el) => {
                          measureRow(rowKey(virtualItem.row), el);
                        }}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                          "will-change": "transform",
                          transition: (() => {
                            const { row } = virtualItem;
                            const isDragging =
                              (row.kind === "pending" || row.kind === "completed") &&
                              activeDragId() === `todo-${row.todo.id}`;
                            return isDragging ? "none" : "transform 280ms var(--ease-snappy)";
                          })(),
                        }}
                        data-index={virtualItem.index}
                      >
                        <Show when={virtualItem.row.kind === "pending"}>
                          <PendingRowItem
                            row={virtualItem.row}
                            onToggle={handleToggleTodo}
                            onDelete={handleDeleteTodo}
                            onSelect={handleSelectTodo}
                            isSelectionMode={isSelectionMode()}
                            selectedIds={selectedIds()}
                            onToggleSelection={toggleItemSelection}
                            listId={props.listId}
                            lists={lists.data}
                            isInitialLoad={rowIsInitialLoad(virtualItem.row)}
                            index={virtualItem.index}
                            exitingIds={exitingIds()}
                            newlyAddedIds={newlyAddedIds()}
                            focusedTodoId={focusedTodoId()}
                            onFocus={handleItemFocus}
                            focusSlide={focusSlide()}
                          />
                        </Show>

                        <Show when={virtualItem.row.kind === "completed-header"}>
                          <CompletedHeaderRowItem
                            row={virtualItem.row}
                            index={virtualItem.index}
                            isKeyboardFocused={focusedIndex() === virtualItem.index}
                            completedCollapsed={completedCollapsed()}
                            onToggleCollapse={() => setCompletedCollapsed(!completedCollapsed())}
                            t={t}
                            onFocus={(index) => setFocusedIndex(index)}
                            focusSlide={focusedIndex() === virtualItem.index ? focusSlide() : null}
                          />
                        </Show>

                        <Show when={virtualItem.row.kind === "completed"}>
                          <CompletedRowItem
                            row={virtualItem.row}
                            onToggle={handleToggleTodo}
                            onDelete={handleDeleteTodo}
                            onSelect={handleSelectTodo}
                            isSelectionMode={isSelectionMode()}
                            selectedIds={selectedIds()}
                            onToggleSelection={toggleItemSelection}
                            listId={props.listId}
                            lists={lists.data}
                            isInitialLoad={rowIsInitialLoad(virtualItem.row)}
                            index={virtualItem.index}
                            exitingIds={exitingIds()}
                            newlyAddedIds={newlyAddedIds()}
                            focusedTodoId={focusedTodoId()}
                            onFocus={handleItemFocus}
                            focusSlide={focusSlide()}
                          />
                        </Show>
                      </div>
                    )}
                  </For>
                </div>
              </Show>

              {/* Infinite-scroll sentinel */}
              <div ref={setTodoSentinelRef} class="h-10 w-full" aria-hidden="true" />
            </Show>
          </SortableProvider>
        </div>
      </div>

      {/* Fixed bottom add-task bar */}
      <TodoListAddBar
        show={props.showAddTodo !== false}
        value={newTodoText()}
        isSubmitting={isSubmitting()}
        onInput={setNewTodoText}
        onSubmit={(e) => void handleAddTodo(e)}
        inputRef={setTodoInput}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={Boolean(todoToDelete())}
        onOpenChange={(open) => {
          if (!open) {
            setTodoToDelete(null);
          }
        }}
        title={t(msg`Delete task?`)}
        description={t(
          msg`Are you sure you want to delete this task? This action cannot be undone.`,
        )}
        confirmLabel={t(msg`Delete`)}
        variant="destructive"
        onConfirm={() => {
          const todo = todoToDelete();
          if (todo) {
            executeDeleteTodo(todo.id);
            setTodoToDelete(null);
          }
        }}
      />
    </div>
  );
}
